import React from 'react';
import { 
  PencilSquareIcon, 
  TrashIcon, 
  EyeIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { DeliveryNote, DeliveryNoteStatus } from '../../types/deliveryNote';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/formatters';
import { Tooltip } from '../common/Tooltip';
import DocumentChain from '../common/DocumentChain';
import InvoiceFromDocumentButton from '../billing/InvoiceFromDocumentButton';

interface DeliveryNoteListProps {
  deliveryNotes: DeliveryNote[];
  onEditClick: (deliveryNote: DeliveryNote) => void;
  onDeleteClick: (deliveryNote: DeliveryNote) => void;
  onViewClick: (deliveryNote: DeliveryNote) => void;
  onStatusChange: (deliveryNoteId: string, status: DeliveryNoteStatus) => void;
  loading?: boolean;
}

const DeliveryNoteList: React.FC<DeliveryNoteListProps> = ({
  deliveryNotes,
  onEditClick,
  onDeleteClick,
  onViewClick,
  onStatusChange,
  loading = false
}) => {
  const { t } = useLanguage();

  const getStatusColor = (status: DeliveryNoteStatus) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'shipped': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'delivered': return 'bg-green-100 text-green-800 border-green-200';
      case 'confirmed': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusBorderColor = (status: DeliveryNoteStatus) => {
    switch (status) {
      case 'draft': return '#94a3b8';
      case 'pending': return '#eab308';
      case 'shipped': return '#2563eb';
      case 'delivered': return '#16a34a';
      case 'confirmed': return '#059669';
      case 'cancelled': return '#dc2626';
      default: return '#94a3b8';
    }
  };

  const getStatusLabel = (status: DeliveryNoteStatus) => {
    switch (status) {
      case 'draft': return 'Vázlat';
      case 'pending': return 'Függőben';
      case 'shipped': return 'Kiszállítva';
      case 'delivered': return 'Leszállítva';
      case 'confirmed': return 'Megerősített';
      case 'cancelled': return 'Törölve';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (deliveryNotes.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-12 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
        <div className="text-gray-500 dark:text-gray-400 text-lg mb-2">Nincs szállítólevél</div>
        <div className="text-gray-400 dark:text-gray-500 text-sm">Itt jelennek meg a szállítólevelek</div>
      </div>
    );
  }

  return (
    <>
      {/* Desktop táblázat nézet */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Szállítólevél szám
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Ügyfél
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Szállítás dátuma
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  Státusz
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  Szállító
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 dark:text-gray-300">
                  Összeg
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 w-20">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {deliveryNotes.map((deliveryNote) => (
                <tr
                  key={deliveryNote.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4"
                  style={{ borderLeftColor: getStatusBorderColor(deliveryNote.status) }}
                >
                  <td className="px-3 py-3">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {deliveryNote.number}
                      </div>
                      {deliveryNote.trackingNumber && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Követési szám: {deliveryNote.trackingNumber}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {deliveryNote.customerName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {deliveryNote.customerEmail}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {new Date(deliveryNote.deliveryDate).toLocaleDateString('hu-HU')}
                      </div>
                      {deliveryNote.expectedDeliveryDate && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Tervezett: {new Date(deliveryNote.expectedDeliveryDate).toLocaleDateString('hu-HU')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <select
                      value={deliveryNote.status}
                      onChange={(e) => onStatusChange(deliveryNote.id, e.target.value as DeliveryNoteStatus)}
                      className={`text-xs font-medium border rounded-full px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 ${getStatusColor(deliveryNote.status)}`}
                    >
                      <option value="draft">Vázlat</option>
                      <option value="pending">Függőben</option>
                      <option value="shipped">Kiszállítva</option>
                      <option value="delivered">Leszállítva</option>
                      <option value="confirmed">Megerősített</option>
                      <option value="cancelled">Törölve</option>
                    </select>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {deliveryNote.carrierName || 'Nincs megadva'}
                      </div>
                      {deliveryNote.shippingMethod && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {deliveryNote.shippingMethod}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right">
                                         {deliveryNote.totalAmount ? (
                       <div className="flex flex-col">
                         <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                           {formatCurrency(deliveryNote.totalAmount, deliveryNote.currency)}
                         </div>
                         <div className="text-xs text-gray-500 dark:text-gray-400">
                           nettó: {formatCurrency(deliveryNote.subtotal || 0, deliveryNote.currency)}
                         </div>
                       </div>
                     ) : (
                       <span className="text-xs text-gray-400">-</span>
                     )}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <InvoiceFromDocumentButton type="delivery_note" showLabel={false} document={{ id: deliveryNote.id, number: deliveryNote.number, customerId: deliveryNote.customerId, items: deliveryNote.items, currency: (deliveryNote as any).currency, invoiceNumber: (deliveryNote as any).invoiceNumber, invoiceType: (deliveryNote as any).invoiceType }} className="text-gray-500 dark:text-gray-400 hover:text-primary-600" />
                      <Tooltip content="Megtekintés">
                        <button
                          onClick={() => onViewClick(deliveryNote)}
                          className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Szerkesztés">
                        <button
                          onClick={() => onEditClick(deliveryNote)}
                          className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Törlés">
                        <button
                          onClick={() => onDeleteClick(deliveryNote)}
                          className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobil kártya nézet */}
      <div className="lg:hidden space-y-4">
        {deliveryNotes.map((deliveryNote) => (
          <div
            key={deliveryNote.id}
            className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700 border-l-4"
            style={{ borderLeftColor: getStatusBorderColor(deliveryNote.status) }}
          >
            {/* Fejléc */}
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {deliveryNote.number}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(deliveryNote.deliveryDate).toLocaleDateString('hu-HU')}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`text-xs font-medium rounded-full px-2 py-1 border ${getStatusColor(deliveryNote.status)}`}>
                  {getStatusLabel(deliveryNote.status)}
                </span>
              </div>
            </div>

            {/* Ügyfél info */}
            <div className="mb-3">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {deliveryNote.customerName}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {deliveryNote.customerEmail}
              </div>
            </div>

            {/* Szállító info */}
            <div className="mb-3">
              <div className="text-sm text-gray-900 dark:text-gray-100">
                <TruckIcon className="h-4 w-4 inline mr-1" />
                {deliveryNote.carrierName || 'Nincs megadva'}
              </div>
              {deliveryNote.trackingNumber && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Követési szám: {deliveryNote.trackingNumber}
                </div>
              )}
            </div>

                         {/* Összeg */}
             {deliveryNote.totalAmount && (
               <div className="mb-3">
                 <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                   {formatCurrency(deliveryNote.totalAmount, deliveryNote.currency)}
                 </div>
                 <div className="text-xs text-gray-500 dark:text-gray-400">
                   nettó: {formatCurrency(deliveryNote.subtotal || 0, deliveryNote.currency)}
                 </div>
               </div>
             )}

            {/* Bizonylat lánc */}
            <div className="mb-3">
              <DocumentChain
                documentId={deliveryNote.id}
                documentType="delivery_note"
                className="text-xs"
              />
            </div>

            {/* Műveletek */}
            <div className="flex justify-end items-center pt-3 border-t border-gray-200 dark:border-gray-600">
              <div className="flex space-x-2">
                <InvoiceFromDocumentButton type="delivery_note" showLabel={false} document={{ id: deliveryNote.id, number: deliveryNote.number, customerId: deliveryNote.customerId, items: deliveryNote.items, currency: (deliveryNote as any).currency, invoiceNumber: (deliveryNote as any).invoiceNumber, invoiceType: (deliveryNote as any).invoiceType }} className="text-gray-500 dark:text-gray-400 hover:text-primary-600" />
                <button
                  onClick={() => onViewClick(deliveryNote)}
                  className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  <EyeIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onEditClick(deliveryNote)}
                  className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDeleteClick(deliveryNote)}
                  className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default DeliveryNoteList; 